import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Form } from 'ui/components/3-organisms/Form/Form';
import { canLoadScript, embedHelper } from './formEmbed';
import { FormFeatureProps } from './formFeature';

const HubSpotFeatureInner: React.FC<FormFeatureProps> = ({ content, selectAllLabel, placeholderLabel, kicker }) => {
	const scriptRef = useRef<HTMLDivElement>(null);
	const [isClient, setIsClient] = useState(false);

	const { isEmbedScript, formHtmlCode, sanitizedFormHtmlCode } = embedHelper(content?.properties?.formHtmlCode);

	/**
	 * Ensure client-side only rendering to prevent SSR hydration mismatches.
	 */
	useEffect(() => {
		setIsClient(true);
	}, []);

	/**
	 * Load HubSpot embed scripts when using script-based forms.
	 * The HubSpotForm component handles all form customization (checkbox groups, buttons, etc.).
	 */
	useEffect(() => {
		if (!isClient || !isEmbedScript || !canLoadScript(scriptRef, formHtmlCode)) return;

		const timer = setTimeout(() => {
			loadHubSpotScript(scriptRef, formHtmlCode);
		}, 100);

		return () => clearTimeout(timer);
	}, [isEmbedScript, formHtmlCode, isClient]);

	if (!isClient) {
		return null;
	}

	return (
		<>
			<Form
				{...content.properties}
				kicker={kicker}
				bgColor={content?.properties.color}
				showImage={content.properties.showImage}
				layoutType={content.properties.layoutType}
				displayButtonRight={content?.properties?.displayButtonRight}
				selectAllLabel={selectAllLabel}
				inputFullWidth={content?.properties?.inputFullWidth}
				placeholderLabel={placeholderLabel}
				formHtmlCode={sanitizedFormHtmlCode}
				buttonStyle={content?.properties?.buttonStyle}
				variant="HubSpot"
			/>
			{isEmbedScript && <div ref={scriptRef} />}
		</>
	);
};

export default dynamic(() => Promise.resolve(HubSpotFeatureInner), { ssr: false });

/**
 * Load HubSpot embed scripts into the DOM.
 * Extracts init and render scripts from the form HTML and executes them sequentially.
 * The HubSpotForm component handles all subsequent form customization.
 */
const loadHubSpotScript = (scriptRef: React.RefObject<HTMLDivElement>, formHtmlCode: string) => {
	if (!scriptRef.current) return;

	const fragment = document.createRange().createContextualFragment(formHtmlCode);

	const [initScript, renderScript] = Array.from(fragment.querySelectorAll('script'));

	if (!initScript) return;

	initScript.defer = true;
	scriptRef.current.appendChild(initScript);

	initScript.onload = () => {
		if (!renderScript) return;

		// Append render script - HubSpotForm component handles checkbox group customization
		const formContainer = scriptRef.current?.previousSibling as HTMLElement | null;
		const hubspotElement = formContainer?.querySelector('.hubspot');

		if (hubspotElement) {
			renderScript.defer = true;
			renderScript.async = true;
			hubspotElement.appendChild(renderScript);
		}
	};
};
